/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	createContext,
	type Dispatch,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import __noop from '@atlaskit/ds-lib/noop';
import SidebarCollapseIcon from '@atlaskit/icon/core/sidebar-collapse';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
import { token } from '@atlaskit/tokens';

type SideNavVisibilityContext = {
	visibleOnDesktop: boolean | null;
	visibleOnMobile: boolean;
	setVisibleOnDesktop: Dispatch<SetStateAction<boolean | null>>;
	setVisibleOnMobile: Dispatch<SetStateAction<boolean>>;
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const SideNavSlotContext = createContext(false);
const SideNavVisibility = createContext<SideNavVisibilityContext>({
	visibleOnDesktop: null,
	visibleOnMobile: false,
	setVisibleOnDesktop: __noop,
	setVisibleOnMobile: __noop,
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const SideNavVisibilityProvider = ({ children }: { children: React.ReactNode }) => {
	const [visibleOnDesktop, setVisibleOnDesktop] = useState<boolean | null>(null);
	const [visibleOnMobile, setVisibleOnMobile] = useState(false);

	const context = useMemo(
		() => ({
			visibleOnDesktop,
			visibleOnMobile,
			setVisibleOnDesktop,
			setVisibleOnMobile,
		}),
		[visibleOnDesktop, visibleOnMobile],
	);

	return <SideNavVisibility.Provider value={context}>{children}</SideNavVisibility.Provider>;
};

export const useSideNavVisibility = ({
	defaultCollapsed = false,
}: {
	/**
	 * The default visibility state of the side nav on desktop screens.
	 * This value will be used if the visibility state is not set in context yet, e.g. during SSR.
	 */
	defaultCollapsed?: boolean;
} = {}): SideNavVisibilityContext & { visibleOnDesktop: boolean } => {
	const { visibleOnDesktop, visibleOnMobile, setVisibleOnDesktop, setVisibleOnMobile } =
		useContext(SideNavVisibility);

	return {
		visibleOnDesktop: visibleOnDesktop === null ? !defaultCollapsed : visibleOnDesktop,
		visibleOnMobile,
		setVisibleOnDesktop,
		setVisibleOnMobile,
	};
};

const styles = cssMap({
	hiddenMobileAndDesktop: {
		display: 'none',
	},
	hiddenMobileOnly: {
		display: 'none',
		'@media (min-width: 64rem)': {
			display: 'initial',
		},
	},
	hiddenDesktopOnly: {
		'@media (min-width: 64rem)': {
			display: 'none',
		},
	},
	absolutelyPositionWithinSideNav: {
		position: 'absolute',
		// Positioning at the top-right corner of the side nav
		insetInlineEnd: token('space.150'),
		insetBlockStart: token('space.150'),
		// Ensure it is above side nav content
		zIndex: 100,
		backgroundColor: token('elevation.surface'),
	},
});

/**
 * __SideNavButton__
 *
 * Button for showing/hiding the side nav.
 *
 * It contains different visibility logic based on the context it is rendered in.
 *
 * To use within the `SideNav`, it should be added to the `collapseButton` slot prop of the `SideNav` component, e.g.
 * ```tsx
 * <SideNavButton collapseButton={<SideNavButton />} />
 * ```
 *
 * When rendered within the side nav:
 * - For mobile screens, the button is never shown.
 * - For desktop screens, the button is only shown when the side nav is visible.
 *
 * When rendered outside the side nav:
 * - For mobile screens, the button is always shown.
 * - For desktop screens, the button is only shown when the side nav is hidden.
 */
export const SideNavButton = ({
	defaultCollapsed = false,
	testId,
}: {
	/**
	 * Whether the side nav should be collapsed by default __on desktop screens__.
	 *
	 * It is always collapsed by default for mobile screens.
	 *
	 * __Note:__ If using this prop, ensure that it is also provided to the `SideNav` slot.
	 * This is to ensure the state is in sync before post-SSR hydration.
	 */
	defaultCollapsed?: boolean;
	testId?: string;
}) => {
	const isWithinSideNavSlot = useContext(SideNavSlotContext);

	const {
		visibleOnDesktop: isSideNavVisibleOnDesktop,
		visibleOnMobile: isSideNavVisibleOnMobile,
		setVisibleOnDesktop: setSideNavVisibleOnDesktop,
		setVisibleOnMobile: setSideNavVisibleOnMobile,
	} = useSideNavVisibility({ defaultCollapsed });

	const [isSideNavVisible, setIsSideNavVisible] = useState<boolean>(!defaultCollapsed);

	useEffect(() => {
		const { matches } = window.matchMedia('(min-width: 64rem)');
		setIsSideNavVisible(matches ? isSideNavVisibleOnDesktop : isSideNavVisibleOnMobile);
	}, [isSideNavVisibleOnDesktop, isSideNavVisibleOnMobile]);

	// When screen size changes, ensure we use the correct visibility state
	useEffect(() => {
		const mediaQueryList = window.matchMedia('(min-width: 64rem)');
		return bind(mediaQueryList, {
			type: 'change',
			listener() {
				setIsSideNavVisible(
					mediaQueryList.matches ? isSideNavVisibleOnDesktop : isSideNavVisibleOnMobile,
				);
			},
		});
	}, [isSideNavVisibleOnDesktop, isSideNavVisibleOnMobile]);

	const toggleVisibility = useCallback(() => {
		const { matches } = window.matchMedia('(min-width: 64rem)');
		if (matches) {
			setSideNavVisibleOnDesktop((prev) => !prev);
		} else {
			setSideNavVisibleOnMobile((prev) => !prev);
		}
	}, [setSideNavVisibleOnDesktop, setSideNavVisibleOnMobile]);

	return (
		<div
			css={[
				isWithinSideNavSlot && styles.absolutelyPositionWithinSideNav,
				isWithinSideNavSlot && isSideNavVisibleOnDesktop && styles.hiddenMobileOnly,
				isWithinSideNavSlot && !isSideNavVisibleOnDesktop && styles.hiddenMobileAndDesktop,
				!isWithinSideNavSlot && isSideNavVisibleOnDesktop && styles.hiddenDesktopOnly,
			]}
			data-testid={testId ? `${testId}-container` : undefined}
		>
			<IconButton
				appearance="subtle"
				label={isSideNavVisible ? 'Collapse sidebar' : 'Expand sidebar'}
				icon={isSideNavVisible ? SidebarCollapseIcon : SidebarExpandIcon}
				onClick={toggleVisibility}
				testId={testId}
				isTooltipDisabled={false}
			/>
		</div>
	);
};
